import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import {
  ArrowLeft, RefreshCw, Download, Layers, AlertTriangle, CalendarClock, Clock,
  ShieldAlert, ClipboardCheck, FileWarning, Camera, Loader2, ChevronDown,
  Sun, TrendingUp, GitCompare, ListChecks, Bookmark, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { TrendsPanel, ComparePanel, ExceptionCenterPanel } from './InsightPanels';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KPIS = [
  { key: 'scheduleOverdue', label: 'Schedule Overdue', sub: 'activities past planned finish', icon: CalendarClock, tone: 'amber' },
  { key: 'qualityOpen', label: 'Open Quality', sub: 'NCR / CAPA / inspections', icon: ClipboardCheck, tone: 'blue' },
  { key: 'safetyOpen', label: 'Open Safety', sub: 'observations / permits / incidents', icon: ShieldAlert, tone: 'red' },
  { key: 'controlsOpen', label: 'Open Controls', sub: 'RFI / submittal / ITP', icon: FileWarning, tone: 'indigo' },
  { key: 'designPending', label: 'Design Pending', sub: 'drawing / reality reviews', icon: FileWarning, tone: 'violet' },
  { key: 'evidence360StaleOrNever', label: '360 Stale / Never', sub: 'capture coverage (not progress)', icon: Camera, tone: 'teal' },
];
const TONE = {
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  violet: 'bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  teal: 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
};

const Cell = ({ value, onClick, highlight }) => {
  if (value === 'restricted') return <span className="text-xs text-slate-400 italic">restricted</span>;
  if (value === null || value === undefined) return <span className="text-xs text-slate-400">—</span>;
  const n = Number(value);
  const zero = n === 0;
  return (
    <button
      onClick={n > 0 ? onClick : undefined}
      disabled={!(n > 0)}
      className={`text-sm font-semibold tabular-nums ${zero ? 'text-slate-400' : highlight ? 'text-red-600 hover:underline' : 'text-slate-800 dark:text-slate-100 hover:underline'} ${n > 0 ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {value}
    </button>
  );
};

const CommandCenter = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [program, setProgram] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [tab, setTab] = useState('overview');
  const [views, setViews] = useState([]);
  const [showViews, setShowViews] = useState(false);

  const loadViews = useCallback(() => {
    axios.get(`${API}/portfolios/${portfolioId}/views`).then((r) => setViews(r.data)).catch(() => {});
  }, [portfolioId]);
  useEffect(() => { loadViews(); }, [loadViews]);

  const saveView = async () => {
    const name = window.prompt('Name this executive view:');
    if (!name) return;
    try {
      await axios.post(`${API}/portfolios/${portfolioId}/views`, { name, kind: tab, programId: program || null });
      toast.success('View saved');
      loadViews();
    } catch { toast.error('Failed to save view'); }
  };
  const openView = async (v) => {
    setShowViews(false);
    try {
      const res = await axios.get(`${API}/portfolios/${portfolioId}/views/${v.id}`);
      const cfg = res.data.config || {};
      setProgram(cfg.programId || '');
      setTab(res.data.kind || 'overview');
      toast.success(`Opened "${res.data.name}"`);
    } catch { toast.error('Failed to open view'); }
  };
  const deleteView = async (id, e) => {
    e.stopPropagation();
    try { await axios.delete(`${API}/portfolios/${portfolioId}/views/${id}`); loadViews(); toast.success('View deleted'); }
    catch { toast.error('Failed'); }
  };

  const TABS = [
    { key: 'overview', label: 'Overview', icon: Layers },
    { key: 'trends', label: 'Trends', icon: TrendingUp },
    { key: 'compare', label: 'Compare', icon: GitCompare },
    { key: 'exceptions', label: 'Exception Center', icon: ListChecks },
  ];

  const load = useCallback(async (opts = {}) => {
    try {
      const params = {};
      if (program) params.programId = program;
      if (opts.refresh) params.refresh = true;
      const res = await axios.get(`${API}/portfolios/${portfolioId}/command`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'Portfolio not found or access not authorized' : 'Failed to load command center');
      if (e.response?.status === 404) navigate('/admin/portfolios');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [portfolioId, program, navigate]);

  useEffect(() => {
    axios.get(`${API}/portfolios/${portfolioId}/programs`).then((r) => setPrograms(r.data)).catch(() => {});
  }, [portfolioId]);
  useEffect(() => { load(); }, [load]);

  const doRefresh = async () => { setRefreshing(true); await load({ refresh: true }); toast.success('Data refreshed'); };

  const doExport = async (type, format) => {
    setShowExport(false);
    setExporting(true);
    try {
      const res = await axios.get(`${API}/portfolios/${portfolioId}/command/export`, {
        params: { type, format }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `portfolio_${type}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${type}.${format}`);
    } catch (e) {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  if (loading) return <Layout><LoadingState message="Aggregating authorized projects..." /></Layout>;
  if (!data) return null;

  const t = data.totals;
  const fr = data.freshness;
  const staleDate = fr.oldestScheduleDataDate;

  return (
    <Layout>
      <div className="space-y-6" data-testid="command-center-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <button onClick={() => navigate(`/admin/portfolios/${portfolioId}`)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-1" data-testid="back-to-portfolio"><ArrowLeft className="w-4 h-4" />Portfolio</button>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Layers className="w-6 h-6 text-indigo-600" />{data.portfolioName}
              <span className="text-sm font-normal text-slate-400">Command Center</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/app/portfolio/${portfolioId}/my-day`)} className="flex items-center gap-2 px-3 py-2 text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 rounded-lg hover:bg-amber-100" data-testid="open-myday">
              <Sun className="w-4 h-4" />My Day
            </button>
            <div className="relative">
              <button onClick={() => setShowViews((s) => !s)} className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50" data-testid="saved-views-button">
                <Bookmark className="w-4 h-4" />Views<ChevronDown className="w-3 h-3" />
              </button>
              {showViews && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 p-1" data-testid="saved-views-menu">
                  <button onClick={saveView} className="w-full text-left px-3 py-2 text-sm text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded" data-testid="save-view-button">+ Save current view</button>
                  {views.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No saved views</div> :
                    views.map((v) => (
                      <div key={v.id} onClick={() => openView(v)} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer" data-testid="saved-view-item">
                        <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{v.name} <span className="text-[10px] text-slate-400">({v.kind})</span></span>
                        <button onClick={(e) => deleteView(v.id, e)} data-testid="delete-view-button"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                      </div>
                    ))}
                </div>
              )}
            </div>
            {programs.length > 0 && (
              <select value={program} onChange={(e) => setProgram(e.target.value)} className="px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="program-filter">
                <option value="">All programs</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <button onClick={doRefresh} disabled={refreshing} className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50" data-testid="refresh-button">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />Refresh
            </button>
            <div className="relative">
              <button onClick={() => setShowExport((s) => !s)} disabled={exporting} className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50" data-testid="export-button">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Export<ChevronDown className="w-3 h-3" />
              </button>
              {showExport && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 p-1" data-testid="export-menu">
                  {['summary', 'exceptions', 'programs', 'freshness'].map((tp) => (
                    <div key={tp} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 rounded">
                      <span className="text-sm capitalize text-slate-700 dark:text-slate-200">{tp}</span>
                      <span className="flex gap-1">
                        <button onClick={() => doExport(tp, 'csv')} className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded" data-testid={`export-${tp}-csv`}>CSV</button>
                        <button onClick={() => doExport(tp, 'xlsx')} className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded" data-testid={`export-${tp}-xlsx`}>XLSX</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 overflow-x-auto" data-testid="command-tabs">
          {TABS.map((tb) => (
            <button key={tb.key} onClick={() => setTab(tb.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === tb.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`} data-testid={`command-tab-${tb.key}`}>
              <tb.icon className="w-4 h-4" />{tb.label}
            </button>
          ))}
        </div>

        {tab === 'trends' && <TrendsPanel portfolioId={portfolioId} />}
        {tab === 'compare' && <ComparePanel portfolioId={portfolioId} />}
        {tab === 'exceptions' && <ExceptionCenterPanel portfolioId={portfolioId} />}

        {tab === 'overview' && (<>
        {/* Freshness banner */}
        <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 text-sm" data-testid="freshness-banner">
          <Clock className="w-4 h-4 text-slate-500 mt-0.5" />
          <div className="text-slate-600 dark:text-slate-300">
            <span className="font-medium">System date:</span> {(fr.systemCurrentDate || '').slice(0, 10)}
            {staleDate && <> · <span className="font-medium">Oldest schedule data date:</span> <span className="text-amber-600">{String(staleDate).slice(0, 10)}</span></>}
            <span className="block text-xs text-slate-400 mt-0.5">Source data date may be older than the system date — stale source data is not implied to be current.</span>
          </div>
        </div>

        {/* Executive Overview */}
        <div>
          <h2 className="text-base font-semibold text-slate-500 uppercase tracking-wide mb-3">Executive Overview · {data.authorizedProjectCount} authorized project(s)</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="kpi-grid">
            {KPIS.map((k) => (
              <div key={k.key} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800" data-testid={`kpi-${k.key}`}>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${TONE[k.tone]}`}><k.icon className="w-5 h-5" /></div>
                <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{t[k.key]}</div>
                <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mt-1">{k.label}</div>
                <div className="text-[11px] text-slate-400">{k.sub}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500">
            <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">Projects with exceptions: <b>{t.projectsWithExceptions}</b></span>
            <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">Progress reviewed available: <b>{t.progressReviewedAvailable}</b></span>
            <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">Progress not configured: <b>{t.progressNotConfigured}</b></span>
            <span className="px-2 py-1 rounded bg-slate-100 dark:bg-slate-800">Safety high/critical: <b>{t.safetyHighCritical}</b></span>
          </div>
        </div>

        {/* Exception Matrix */}
        <div>
          <h2 className="text-base font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Project Exception Matrix</h2>
          {data.projects.length === 0 ? (
            <div className="text-center py-12 text-slate-500 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800" data-testid="matrix-empty">
              No authorized projects to display for this portfolio{program ? ' / program' : ''}.
            </div>
          ) : (
            <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800" data-testid="exception-matrix">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
                    <th className="px-4 py-3">Project</th>
                    <th className="px-3 py-3">Schedule</th>
                    <th className="px-3 py-3">Progress</th>
                    <th className="px-3 py-3">Quality</th>
                    <th className="px-3 py-3">Safety</th>
                    <th className="px-3 py-3">Controls</th>
                    <th className="px-3 py-3">Design</th>
                    <th className="px-3 py-3">360</th>
                    <th className="px-3 py-3">Last Data</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.projects.map((r) => (
                    <tr key={r.projectId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`matrix-row-${r.projectId}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => navigate(`/app/projects/${r.projectId}/workspace`)} className="font-medium text-slate-900 dark:text-white hover:underline text-left" data-testid={`matrix-project-${r.projectId}`}>{r.name}</button>
                        <div className="text-[11px] text-slate-400 capitalize">{r.status}</div>
                      </td>
                      <td className="px-3 py-3"><Cell value={r.schedule ? r.schedule.overdue : 'restricted'} highlight onClick={() => navigate(r.links.schedule)} /></td>
                      <td className="px-3 py-3">
                        {!r.progress ? <span className="text-xs text-slate-400 italic">restricted</span>
                          : r.progress.available ? <button onClick={() => navigate(r.links.progress)} className="text-sm font-semibold hover:underline text-slate-800 dark:text-slate-100">{r.progress.reviewedVisual}%{r.progress.variancePp != null && <span className={`ml-1 text-xs ${r.progress.variancePp < 0 ? 'text-red-500' : 'text-emerald-500'}`}>({r.progress.variancePp > 0 ? '+' : ''}{r.progress.variancePp}pp)</span>}</button>
                          : <span className="text-[11px] text-slate-400" data-testid="progress-not-configured">Not configured</span>}
                      </td>
                      <td className="px-3 py-3"><Cell value={r.quality.open} onClick={() => navigate(r.links.quality)} /><span className="text-[11px] text-slate-400 ml-1">{r.quality.overdue ? `${r.quality.overdue} od` : ''}</span></td>
                      <td className="px-3 py-3"><Cell value={r.safety.open} highlight={r.safety.highCritical > 0} onClick={() => navigate(r.links.safety)} />{r.safety.highCritical > 0 && <span className="text-[11px] text-red-500 ml-1">{r.safety.highCritical} hi</span>}</td>
                      <td className="px-3 py-3"><Cell value={r.controls.open} onClick={() => navigate(r.links.controls)} /><span className="text-[11px] text-slate-400 ml-1">{r.controls.overdue ? `${r.controls.overdue} od` : ''}</span></td>
                      <td className="px-3 py-3"><Cell value={r.design ? (r.design.pendingChanges + r.design.pendingRealityReviews) : 'restricted'} onClick={() => navigate(r.links.design)} /></td>
                      <td className="px-3 py-3"><Cell value={r.evidence360 ? r.evidence360.staleOrNever : null} onClick={() => navigate(r.links.evidence360)} /></td>
                      <td className="px-3 py-3 text-xs text-slate-500 tabular-nums">{(r.freshness && r.freshness.scheduleDataDate) ? String(r.freshness.scheduleDataDate).slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-400 border-t border-slate-100 dark:border-slate-800 pt-4" data-testid="governance-note">{data.governanceNote}</p>
        </>)}
      </div>
    </Layout>
  );
};

export default CommandCenter;
